import {
  Box,
  Card,
  CardContent,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { PARTIAL_DATA_WARNING, STATUS_COLORS } from '../shared/AppConstants';
import { formatCurrency, formatPieces } from './SupplierSpendShift';

function calculatePercentageChange(currentValue, previousValue) {
  if (previousValue === 0) {
    return null; // Cannot calculate percentage change from zero
  }
  const change = ((currentValue - previousValue) / previousValue) * 100;
  // Symmetric rounding: round absolute value then reapply sign
  return change >= 0 ? Math.round(change) : -Math.round(Math.abs(change));
}

function SpendSummaryBox({
  label,
  amount,
  percentageChange,
  spendShiftDisplayMode,
  hasError
}) {
  const formattedAmount =
    spendShiftDisplayMode === 'pieces'
      ? formatPieces(amount)
      : formatCurrency(amount);

  const showChange =
    percentageChange !== undefined && percentageChange !== null;
  const isPositive = showChange && percentageChange > 0;
  const isNegative = showChange && percentageChange < 0;
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        outline: hasError
          ? `2px solid ${theme.fuel6Tokens.JddcColorBorderWarning}`
          : undefined
      }}
    >
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: theme.fuel6Tokens.JddcSpacingSmall,
          padding: `calc(${theme.fuel6Tokens.JddcSpacingSmall} + ${theme.fuel6Tokens.JddcSpacingXSmall})`,
          height: '100%',
          boxSizing: 'border-box',
          '&:last-child': {
            paddingBottom: `calc(${theme.fuel6Tokens.JddcSpacingSmall} + ${theme.fuel6Tokens.JddcSpacingXSmall})`
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Typography
            variant="bodyMedium"
            fontWeight={theme.fuel6Tokens.JddcTypographyFontWeightBold}
            fontSize={theme.fuel6Tokens.JddcTypographyHeading2FontSize}
            lineHeight={theme.fuel6Tokens.JddcTypographyHeading2LineHeight}
          >
            {formattedAmount}
          </Typography>
          {hasError && (
            <Tooltip title={PARTIAL_DATA_WARNING} placement="top">
              <span tabIndex={0} style={{ display: 'inline-flex' }}>
                <Box
                  component="span"
                  aria-label={PARTIAL_DATA_WARNING}
                  sx={{
                    color: theme.fuel6Tokens.JddcColorIconWarning,
                    fontSize: theme.fuel6Tokens.JddcIconSizeSmall,
                    mt: theme.fuel6Tokens.JddcSpacingXSmall,
                    display: 'inline-flex'
                  }}
                >
                  <fuel-icon name="warning" />
                </Box>
              </span>
            </Tooltip>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography
            variant="bodySmall"
            color="text.secondary"
            fontSize={theme.fuel6Tokens.JddcTypographyLabelLargeFontSize}
            lineHeight={theme.fuel6Tokens.JddcTypographyLabelLargeLineHeight}
          >
            {label}
          </Typography>
          {showChange && (
            <Typography
              variant="bodySmall"
              fontWeight={theme.fuel6Tokens.JddcTypographyFontWeightBold}
              fontSize={theme.fuel6Tokens.JddcTypographyLabelLargeFontSize}
              color={
                isPositive
                  ? STATUS_COLORS.POSITIVE
                  : isNegative
                    ? STATUS_COLORS.NEGATIVE
                    : 'inherit'
              }
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              ({percentageChange}%)
              {isPositive && (
                <Box component="span" data-testid="trend-up-icon" sx={{ color: STATUS_COLORS.POSITIVE, fontSize: theme.fuel6Tokens.JddcIconSizeMedium, display: 'inline-flex' }}>
                  <fuel-icon name="trend_up" />
                </Box>
              )}
              {isNegative && (
                <Box component="span" data-testid="trend-down-icon" sx={{ color: STATUS_COLORS.NEGATIVE, fontSize: theme.fuel6Tokens.JddcIconSizeMedium, display: 'inline-flex' }}>
                  <fuel-icon name="trend_down" />
                </Box>
              )}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function SupplierSpendShiftSummary({
  supplierSpendData,
  spendShiftDisplayMode,
  errorStatus
}) {
  const totalSpends = supplierSpendData.totalSpends;

  const r12InvoicedAmount = totalSpends.r12InvoiceSpend.amount;
  const r12ForecastAmount = totalSpends.r12ForecastSpend.amount;

  const r12ForecastChange = calculatePercentageChange(
    r12ForecastAmount,
    r12InvoicedAmount
  );

  // Use minimum length across both arrays (capped at 3)
  const maxFiscalYears = Math.min(
    supplierSpendData.fiscalYears.length,
    totalSpends.fiscalYears.length,
    3
  );

  const fiscalYearLabels = supplierSpendData.fiscalYears.slice(
    0,
    maxFiscalYears
  );
  const fiscalYearsData = totalSpends.fiscalYears.slice(0, maxFiscalYears);

  const fiscalYearChanges = fiscalYearsData.map((fyEntry, index) => {
    const previousAmount =
      index === 0 ? r12ForecastAmount : fiscalYearsData[index - 1].amount;
    return calculatePercentageChange(fyEntry.amount, previousAmount);
  });

  return (
    <Grid container spacing={2} columns={{ xs: 12, sm: 12, md: 5 }}>
      <Grid size={{ xs: 12, sm: 6, md: 1 }}>
        <SpendSummaryBox
          label="R12 Invoiced"
          amount={r12InvoicedAmount}
          spendShiftDisplayMode={spendShiftDisplayMode}
          hasError={!!errorStatus?.invoiceSpendError}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6, md: 1 }}>
        <SpendSummaryBox
          label="R12 Forecast"
          amount={r12ForecastAmount}
          percentageChange={r12ForecastChange}
          spendShiftDisplayMode={spendShiftDisplayMode}
          hasError={!!errorStatus?.forecastSpendError}
        />
      </Grid>
      {fiscalYearsData.map((fyEntry, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 1 }} key={fiscalYearLabels[index]}>
          <SpendSummaryBox
            label={`FY${fiscalYearLabels[index]}`}
            amount={fyEntry.amount}
            percentageChange={fiscalYearChanges[index]}
            spendShiftDisplayMode={spendShiftDisplayMode}
            hasError={!!errorStatus?.forecastSpendError}
          />
        </Grid>
      ))}
    </Grid>
  );
}
